import _ from 'lodash';
import produce from 'immer';
import create from 'zustand';
import moment from 'moment-timezone';
import { TZ } from '../../../../shared/consts';
import { INPUT_FMT, AUDIENCE_STATS } from '../../common/constants';

const setGranularity = (daysAgo, granularity) => (Number.isFinite(daysAgo) ? 'm' : granularity);

const mapFollowersWithStatsFilter = daysAgo => {
  if (!Number.isFinite(daysAgo)) {
    return d => d;
  }
  const earliest = moment.tz(TZ).startOf('day').subtract(daysAgo - 1, 'days');
  const validDate = (v, date) => earliest.isSameOrBefore(moment.tz(date, INPUT_FMT, TZ), 'day');
  return d => ({ ...d, followers: _.pickBy(d.followers, validDate) });
};

const filterFollowersWithContributors = contributors => {
  const selectedSlug = contributors.filter(c => c.selected).map(c => c.slug);
  return f => (selectedSlug.indexOf(f.slug) >= 0);
};

const convertPayload = payload => (payload || []).map(contributor => {
  const c = _.pickBy(contributor, (v, k) => AUDIENCE_STATS.indexOf(k) < 0);
  const timeseries = {};
  AUDIENCE_STATS.forEach(key => {
    _.get(contributor, key, []).forEach(item => {
      const date = _.get(item, 'date');
      const count = _.get(item, 'count');
      if (date && (typeof count === 'number')) {
        timeseries[date] = timeseries[date] || { gained: 0, lost: 0 };
        timeseries[date][key] += count;
      }
    });
  });

  // This is possible because we always query for the fullset data, which
  // means initially there was 0 followers.
  let total = 0;
  // sorting essential, the accumulative only works when we sort the
  // timeseries with chronical order
  const dates = Object.keys(timeseries).sort();
  dates.forEach(date => {
    const gained = _.get(timeseries[date], AUDIENCE_STATS[0], 0);
    const lost = _.get(timeseries[date], AUDIENCE_STATS[1], 0);
    // The accumTotal prop represents the accumulative total number after the
    // date's gains and losses.
    total += gained - lost;
    timeseries[date].accumTotal = total;
  });

  if (total) {
    const t = moment.tz(_.last(dates), INPUT_FMT, TZ).add(1, 'day');
    const today = moment.tz(TZ);
    while (!t.isAfter(today, 'day')) {
      timeseries[t.format(INPUT_FMT)] = { accumTotal: total, gained: 0, lost: 0 };
      t.add(1, 'day');
    }
  }

  c.followers = timeseries;
  return c;
});

const setFollowers = (rawFollowers, daysAgo, contributors) => {
  const list = rawFollowers;
  if (!Number.isFinite(daysAgo) && contributors.every(c => c.selected)) {
    return [...list];
  }
  return list
    .filter(filterFollowersWithContributors(contributors))
    .map(mapFollowersWithStatsFilter(daysAgo));
};

const useFollowersStore = create(set => ({
  requestData: false,
  granularity: 'm',
  loading: false,
  /**
   * rawFollowers and followers now has the following structure:
   * [
   *   {
   *     id: number,
   *     name: string,
   *     slug: string,
   *     createdAt: string,
   *     followers: {
   *       '2020-07-19': {
   *         accumTotal: number,
   *         gained: number,
   *         lost: number,
   *       },
   *       ...
   *     },
   *   }
   *   ...
   * ]
   */
  rawFollowers: [],
  followers: [],
  daysAgo: Infinity,
  generatedAt: moment(),
  contributors: [],
  set: fn => set(produce(fn)),
  setRawFollowers: d => set(state => {
    const rawFollowers = convertPayload(d);
    const followers = setFollowers(rawFollowers, state.daysAgo, state.contributors);
    return { rawFollowers, followers };
  }),
  setDaysAgo: d => set(state => ({
    daysAgo: d,
    followers: setFollowers(state.rawFollowers, d, state.contributors),
    granularity: setGranularity(d, state.granularity),
  })),
  setContributors: d => set(state => ({
    contributors: d || [],
    followers: setFollowers(state.rawFollowers, state.daysAgo, d || []),
  })),
}));

export default useFollowersStore;
export { mapFollowersWithStatsFilter };
